import {DialogsEnum} from "@/lib/enums/DialogsEnum";
import {DialogRequest, OrchestratorContext} from "./types";

/**
 * Holds pending dialog requests and answers "what should we consider next?".
 *
 * Priority is **context-aware**, so it is intentionally NOT baked in at enqueue
 * time — the queue stores requests and sorts lazily using a priority function
 * supplied by the orchestrator at evaluation time. This is what lets the same
 * dialog outrank another only on a specific route / flow / auth state.
 *
 * Delayed scheduling is modelled by `scheduledFor`; the orchestrator owns the
 * timer and asks the queue for "ready" items.
 */
export class QueueManager {
    private items: DialogRequest[] = [];

    enqueue(request: DialogRequest): void {
        this.items.push(request);
    }

    /** Is there a queued request for this id? (used for dedupe / dependencies) */
    hasId(id: DialogsEnum): boolean {
        return this.items.some((r) => r.id === id);
    }

    has(instanceId: string): boolean {
        return this.items.some((r) => r.instanceId === instanceId);
    }

    get(instanceId: string): DialogRequest | undefined {
        return this.items.find((r) => r.instanceId === instanceId);
    }

    /** Remove a specific instance; returns it if it was present. */
    remove(instanceId: string): DialogRequest | undefined {
        const idx = this.items.findIndex((r) => r.instanceId === instanceId);
        if (idx === -1) return undefined;
        return this.items.splice(idx, 1)[0];
    }

    /** Cancel every queued request for an id; returns the removed ones. */
    cancelById(id: DialogsEnum): DialogRequest[] {
        const removed = this.items.filter((r) => r.id === id);
        this.items = this.items.filter((r) => r.id !== id);
        return removed;
    }

    clear(): DialogRequest[] {
        const all = this.items;
        this.items = [];
        return all;
    }

    size(): number {
        return this.items.length;
    }

    snapshot(): readonly DialogRequest[] {
        return this.items;
    }

    /** Earliest future `scheduledFor` among not-yet-ready items, or null. */
    nextWakeupAt(now: number): number | null {
        let next: number | null = null;
        for (const r of this.items) {
            if (r.scheduledFor > now && (next === null || r.scheduledFor < next)) {
                next = r.scheduledFor;
            }
        }
        return next;
    }

    /**
     * Items eligible *right now* (delay elapsed), sorted by descending
     * context-aware priority. Ties broken by FIFO (earlier enqueue first).
     */
    ready(
        now: number,
        priorityOf: (r: DialogRequest) => number,
    ): DialogRequest[] {
        return this.items
            .filter((r) => r.scheduledFor <= now)
            .map((r) => ({r, p: priorityOf(r)}))
            .sort((a, b) => (b.p - a.p) || (a.r.enqueuedAt - b.r.enqueuedAt))
            .map((x) => x.r);
    }
}

/** Lightweight unique id generator (no crypto dependency required). */
export function makeInstanceId(id: DialogsEnum): string {
    return `${id}:${Date.now().toString(36)}:${Math.random().toString(36).slice(2, 8)}`;
}

/** Build a normalized request from an id and options. */
export function buildRequest(
    id: DialogsEnum,
    opts: {
        instanceId?: string;
        props?: Record<string, unknown>;
        mode?: DialogRequest["mode"];
        delay?: number;
        source?: DialogRequest["source"];
        meta?: Record<string, unknown>;
    },
    now: number,
): DialogRequest {
    return {
        id,
        instanceId: opts.instanceId ?? makeInstanceId(id),
        props: opts.props,
        mode: opts.mode,
        source: opts.source,
        meta: opts.meta,
        enqueuedAt: now,
        scheduledFor: now + (opts.delay ?? 0),
        phase: "requested",
    };
}

/** Helper kept here so callers don't import the whole context type for sorting. */
export type PriorityResolver = (r: DialogRequest, ctx: OrchestratorContext) => number;
